import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, Check, ArrowRight, RotateCcw, CheckCircle2, Trophy, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { showRewards } from "@/lib/rewards";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export default function LessonPlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/lessons/${id}`).then((r) => {
      if (r.data.locked) { toast.error("This masterclass is Premium"); navigate("/app/premium"); return; }
      setLesson(r.data);
    }).catch(() => navigate(-1));
  }, [id]);

  const complete = async (score) => {
    try {
      const { data } = await api.post("/lessons/complete", { lesson_id: id, score });
      setResult(data);
      showRewards(data);
      refreshUser();
      setDone(true);
    } catch { setDone(true); }
  };

  if (!lesson) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="animate-spin text-indigo-400" size={32} /></div>;

  return (
    <div className="min-h-screen aurora bg-background text-foreground">
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="font-display font-bold text-sm truncate">{lesson.title}</span>
          <button data-testid="close-lesson-btn" onClick={() => navigate(-1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/5"><X size={20} /></button>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-4 py-8 pb-32">
        <AnimatePresence mode="wait">
          {done ? (
            <Completion key="done" result={result} lesson={lesson} onExit={() => navigate(-1)} />
          ) : (
            <motion.div key="body" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {lesson.type === "quick_read" && <QuickRead lesson={lesson} onComplete={() => complete(100)} />}
              {lesson.type === "masterclass" && <Masterclass lesson={lesson} onComplete={() => complete(100)} />}
              {lesson.type === "quiz" && <Quiz lesson={lesson} onComplete={complete} />}
              {lesson.type === "flashcards" && <Flashcards lesson={lesson} onComplete={() => complete(100)} />}
              {lesson.type === "checklist" && <Checklist lesson={lesson} onComplete={() => complete(100)} />}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Masterclass({ lesson, onComplete }) {
  const c = lesson.content;
  return (
    <div>
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/25 mb-3">Premium Masterclass</span>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-1">{lesson.title}</h1>
      <p className="text-xs text-muted-foreground mb-7">{c.minutes} min · +{lesson.xp} XP</p>
      <div className="space-y-6">
        {(c.sections || []).map((s, i) => (
          <div key={i} className="glass rounded-2xl p-5 border border-white/10">
            <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><span className="h-6 w-6 rounded-lg bg-yellow-500/15 text-yellow-300 grid place-items-center text-xs font-bold">{i + 1}</span>{s.heading}</h2>
            <p className="text-[15px] leading-relaxed text-muted-foreground">{s.body}</p>
          </div>
        ))}
      </div>
      {c.keyPoints?.length > 0 && (
        <div className="mt-6 glass rounded-2xl p-5 border border-indigo-500/20">
          <h3 className="font-display font-bold text-sm mb-3">Key Takeaways</h3>
          <ul className="space-y-2">{c.keyPoints.map((k, i) => <li key={i} className="flex gap-2.5 text-sm"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />{k}</li>)}</ul>
        </div>
      )}
      {c.worksheet?.length > 0 && (
        <div className="mt-4 glass rounded-2xl p-5 border border-yellow-500/25">
          <h3 className="font-display font-bold text-sm mb-3">Action Worksheet</h3>
          <ul className="space-y-2">{c.worksheet.map((w, i) => <li key={i} className="flex gap-2.5 text-sm"><span className="text-yellow-400 font-bold">{i + 1}.</span>{w}</li>)}</ul>
        </div>
      )}
      <FixedCTA><Button data-testid="complete-lesson-btn" onClick={onComplete} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">Complete Masterclass <ArrowRight size={18} className="ml-1" /></Button></FixedCTA>
    </div>
  );
}

function QuickRead({ lesson, onComplete }) {
  const c = lesson.content;
  const paras = (c.body || "").split("\n\n");
  const render = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) => p.startsWith("**") ? <strong key={i} className="text-foreground font-bold">{p.slice(2, -2)}</strong> : p);
  return (
    <div>
      <span className="text-xs uppercase tracking-widest font-bold text-indigo-400">{c.minutes} min read</span>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mt-2 mb-6">{lesson.title}</h1>
      <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
        {paras.map((p, i) => <p key={i}>{render(p)}</p>)}
      </div>
      {c.keyPoints?.length > 0 && (
        <div className="mt-7 glass rounded-2xl p-5 border border-indigo-500/20">
          <h3 className="font-display font-bold text-sm mb-3 flex items-center gap-2"><Sparkles size={15} className="text-indigo-400" /> Key Takeaways</h3>
          <ul className="space-y-2">
            {c.keyPoints.map((k, i) => <li key={i} className="flex gap-2.5 text-sm"><Check size={16} className="text-emerald-400 shrink-0 mt-0.5" />{k}</li>)}
          </ul>
        </div>
      )}
      <FixedCTA><Button data-testid="complete-lesson-btn" onClick={onComplete} className="w-full h-13 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">Mark Complete <ArrowRight size={18} className="ml-1" /></Button></FixedCTA>
    </div>
  );
}

function Quiz({ lesson, onComplete }) {
  const qs = lesson.content.questions;
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);
  const q = qs[idx];

  const choose = (i) => { if (picked !== null) return; setPicked(i); if (i === q.answer_index) setCorrect((c) => c + 1); };
  const nextQ = () => {
    if (idx < qs.length - 1) { setIdx(idx + 1); setPicked(null); }
    else onComplete(Math.round((correct / qs.length) * 100));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-widest font-bold text-indigo-400">Question {idx + 1}/{qs.length}</span>
        <span className="text-xs text-muted-foreground font-semibold">{correct} correct</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 mb-7 overflow-hidden"><div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${((idx) / qs.length) * 100}%` }} /></div>
      <h2 className="font-display font-extrabold text-2xl tracking-tight mb-6">{q.q}</h2>
      <div className="space-y-3">
        {q.options.map((o, i) => {
          let cls = "bg-white/5 border-white/10 hover:border-white/25";
          if (picked !== null) {
            if (i === q.answer_index) cls = "bg-emerald-500/15 border-emerald-500/50 text-emerald-300";
            else if (i === picked) cls = "bg-rose-500/15 border-rose-500/50 text-rose-300";
            else cls = "bg-white/5 border-white/10 opacity-60";
          }
          return (
            <button key={i} data-testid={`quiz-option-${i}`} onClick={() => choose(i)} disabled={picked !== null}
              className={`w-full text-left px-5 py-4 rounded-2xl border font-semibold text-[15px] transition-all flex items-center justify-between ${cls}`}>
              {o}
              {picked !== null && i === q.answer_index && <Check size={18} className="text-emerald-400" />}
              {picked !== null && i === picked && i !== q.answer_index && <X size={18} className="text-rose-400" />}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5 glass rounded-2xl p-4 text-sm text-muted-foreground">
          <strong className="text-foreground">Why:</strong> {q.explanation}
        </motion.div>
      )}
      <FixedCTA><Button data-testid="quiz-next-btn" onClick={nextQ} disabled={picked === null} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">{idx < qs.length - 1 ? "Next" : "Finish"} <ArrowRight size={18} className="ml-1" /></Button></FixedCTA>
    </div>
  );
}

function Flashcards({ lesson, onComplete }) {
  const cards = lesson.content.cards;
  const [idx, setIdx] = useState(0);
  const [flip, setFlip] = useState(false);
  const card = cards[idx];
  const next = () => { if (idx < cards.length - 1) { setIdx(idx + 1); setFlip(false); } else onComplete(); };
  return (
    <div>
      <span className="text-xs uppercase tracking-widest font-bold text-indigo-400">Card {idx + 1}/{cards.length}</span>
      <h1 className="font-display font-extrabold text-2xl tracking-tight mt-2 mb-6">{lesson.title}</h1>
      <button data-testid="flashcard" onClick={() => setFlip(!flip)} className="w-full min-h-[240px] relative" style={{ perspective: 1000 }}>
        <motion.div className="w-full min-h-[240px] relative" animate={{ rotateY: flip ? 180 : 0 }} transition={{ duration: 0.5 }} style={{ transformStyle: "preserve-3d" }}>
          <div className="absolute inset-0 glass rounded-3xl p-8 grid place-items-center text-center border border-indigo-500/20" style={{ backfaceVisibility: "hidden" }}>
            <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Question</span><p className="font-display font-bold text-xl mt-3">{card.front}</p><p className="text-xs text-muted-foreground mt-5">Tap to reveal</p></div>
          </div>
          <div className="absolute inset-0 glass rounded-3xl p-8 grid place-items-center text-center border border-emerald-500/25 bg-emerald-500/5" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
            <div><span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Answer</span><p className="text-[15px] leading-relaxed mt-3">{card.back}</p></div>
          </div>
        </motion.div>
      </button>
      <FixedCTA><Button data-testid="flashcard-next-btn" onClick={next} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">{idx < cards.length - 1 ? "Next Card" : "Finish"} <ArrowRight size={18} className="ml-1" /></Button></FixedCTA>
    </div>
  );
}

function Checklist({ lesson, onComplete }) {
  const items = lesson.content.items;
  const [checked, setChecked] = useState([]);
  const toggle = (i) => setChecked((c) => c.includes(i) ? c.filter((x) => x !== i) : [...c, i]);
  return (
    <div>
      <span className="text-xs uppercase tracking-widest font-bold text-indigo-400">Action Checklist</span>
      <h1 className="font-display font-extrabold text-2xl tracking-tight mt-2 mb-6">{lesson.title}</h1>
      <div className="space-y-2.5">
        {items.map((it, i) => (
          <button key={i} data-testid={`checklist-item-${i}`} onClick={() => toggle(i)} className={`w-full flex items-center gap-3 rounded-2xl p-4 text-left border transition-all ${checked.includes(i) ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/5 border-white/10"}`}>
            <div className={`h-6 w-6 rounded-lg grid place-items-center shrink-0 border ${checked.includes(i) ? "bg-emerald-500 border-emerald-500" : "border-white/20"}`}>{checked.includes(i) && <Check size={15} className="text-white" />}</div>
            <span className={`text-sm font-medium ${checked.includes(i) ? "line-through text-muted-foreground" : ""}`}>{it}</span>
          </button>
        ))}
      </div>
      <FixedCTA><Button data-testid="checklist-complete-btn" onClick={onComplete} className="w-full py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">Complete <ArrowRight size={18} className="ml-1" /></Button></FixedCTA>
    </div>
  );
}

function FixedCTA({ children }) {
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 glass border-t border-border/50">
      <div className="max-w-3xl mx-auto px-4 py-4">{children}</div>
    </div>
  );
}

function Completion({ result, lesson, onExit }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }} className="h-24 w-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-400 grid place-items-center mx-auto mb-6 shadow-2xl shadow-emerald-500/40">
        <CheckCircle2 size={48} className="text-white" />
      </motion.div>
      <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">Lesson Complete!</h1>
      <p className="text-muted-foreground mb-7">{lesson.title}</p>
      <div className="flex justify-center gap-3 mb-8">
        <div className="glass rounded-2xl px-6 py-4"><div className="text-2xl font-display font-extrabold text-yellow-400">+{result?.xp_gained ?? 0}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Life XP</div></div>
        <div className="glass rounded-2xl px-6 py-4"><div className="text-2xl font-display font-extrabold text-emerald-400">+{result?.coins_gained ?? 0}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold">Coins</div></div>
      </div>
      {result?.new_badges?.length > 0 && (
        <div className="glass rounded-2xl p-4 mb-8 inline-flex items-center gap-2 border border-yellow-500/25"><Trophy size={18} className="text-yellow-400" /><span className="text-sm font-semibold">Badge unlocked!</span></div>
      )}
      <Button data-testid="finish-lesson-btn" onClick={onExit} className="w-full max-w-xs mx-auto flex py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-base">Continue</Button>
    </motion.div>
  );
}
